import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api.ts';
import { money, pnlClass } from '../format.ts';
import { Icon } from './Icons.tsx';

// Every ticker in the app opens its Research page the same way, through these.

export const researchPath = (symbol: string) => `/research/${encodeURIComponent(symbol.toUpperCase())}`;

export function TickerLink({ symbol, className, title, children }: { symbol: string; className?: string; title?: string; children?: ReactNode }) {
  return <Link to={researchPath(symbol)} className={className} title={title ?? `Research ${symbol.toUpperCase()}`}>{children ?? symbol}</Link>;
}

// Tickers you've opened in Research, newest first (this device only).
const RECENT_KEY = 'tj_research_recent';
export function readRecent(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]'); } catch { return []; }
}
export function rememberTicker(symbol: string) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify([symbol, ...readRecent().filter((s) => s !== symbol)].slice(0, 12))); } catch { /* ignore */ }
}
function clearRecent() {
  try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
}

type Hit = { symbol: string; name: string; trades: number };
const MAC = /Mac|iPhone|iPad/.test(navigator.platform);
type Mine = { days: number; tickers: { symbol: string; name: string | null; trades: number; net: number }[] };

/** The typed text highlighted where it first appears. */
function Marked({ text, q }: { text: string; q: string }) {
  const i = q ? text.toUpperCase().indexOf(q.toUpperCase()) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

/**
 * Ticker search with autocomplete from SEC's ticker list. Before you type it offers your recent and most-traded
 * tickers; `hotkey` makes Ctrl/⌘ K and / jump to it from anywhere (the one above every page).
 */
export function TickerSearch({ autoFocus, className, hotkey }: { autoFocus?: boolean; className?: string; hotkey?: boolean }) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<{ q: string; list: Hit[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [mine, setMine] = useState<Mine | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const [active, setActive] = useState(0);
  const seq = useRef(0);
  const query = q.trim();

  useEffect(() => {
    if (!query) { setHits(null); setFailed(false); return; }
    const n = ++seq.current;
    const t = setTimeout(() => {
      api.get<Hit[]>(`/api/research/search?q=${encodeURIComponent(query)}`)
        .then((list) => { if (n === seq.current) { setHits({ q: query, list }); setFailed(false); setActive(0); } })
        .catch(() => { if (n === seq.current) setFailed(true); });
    }, 150);
    return () => clearTimeout(t);
  }, [query]);

  // Your tickers load when the panel first opens, then refresh each time it opens again.
  useEffect(() => {
    if (!open) return;
    setRecent(readRecent().slice(0, 5));
    setActive(0);
    api.get<Mine>('/api/research/mine').then(setMine).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!hotkey) return;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
      if ((e.key === 'k' && (e.ctrlKey || e.metaKey)) || (e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey)) {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hotkey]);

  // What the arrow keys move through: matches while typing, otherwise recent then your tickers.
  const myTickers = mine?.tickers ?? [];
  const options: string[] = query ? (hits?.list ?? []).map((h) => h.symbol) : [...recent, ...myTickers.map((t) => t.symbol)];
  const pos = Math.min(active, Math.max(0, options.length - 1));
  const close = () => { setOpen(false); input.current?.blur(); };
  const go = (s: string) => { setQ(''); setHits(null); close(); navigate(researchPath(s)); };
  const optId = (i: number) => `ts-opt-${i}`;

  // While the next results load, the last ones stay up rather than flashing "Searching…" at every keystroke.
  const shownHits = hits?.list ?? [];
  let panel: ReactNode = null;
  if (open && query) {
    panel = failed ? <div className="ts-note">Search isn't available right now. Try again in a moment.</div>
      : !hits ? <div className="ts-note">Searching…</div>
      : hits.q === query && !hits.list.length ? <div className="ts-note">No tickers match “{query}”. Press Enter to open {query.toUpperCase()} anyway.</div>
      : (
        <>
          <div className="ts-sec"><span>Matches</span>{shownHits.some((h) => h.trades > 0) && <span className="ts-hint">your trades, all time</span>}</div>
          {shownHits.map((h, i) => (
            <div key={h.symbol} id={optId(i)} role="option" aria-selected={i === pos} className={`ts-row ${i === pos ? 'on' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); go(h.symbol); }} onMouseMove={() => setActive(i)}>
              <b className="ts-sym"><Marked text={h.symbol} q={query} /></b>
              <span className="ts-name"><Marked text={h.name} q={query} /></span>
              <span className="ts-meta">{h.trades > 0 ? `${h.trades} trade${h.trades === 1 ? '' : 's'}` : ''}</span>
              <span className="ts-enter" aria-hidden="true">{i === pos ? 'Open ↵' : ''}</span>
            </div>
          ))}
        </>
      );
  } else if (open && options.length) {
    panel = (
      <>
        {recent.length > 0 && (
          <>
            <div className="ts-sec"><span>Recent</span>
              <button type="button" className="ts-clear" onMouseDown={(e) => { e.preventDefault(); clearRecent(); setRecent([]); setActive(0); }}>Clear</button>
            </div>
            <div className="ts-chips">
              {recent.map((s, i) => (
                <span key={s} id={optId(i)} role="option" aria-selected={i === pos} className={`ts-chip ${i === pos ? 'on' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); go(s); }} onMouseMove={() => setActive(i)}>
                  <Icon.clock />{s}
                </span>
              ))}
            </div>
          </>
        )}
        {myTickers.length > 0 && (
          <>
            <div className="ts-sec"><span>Your tickers</span><span className="ts-hint">last {mine!.days} days</span></div>
            {myTickers.map((t, j) => {
              const i = recent.length + j;
              return (
                <div key={t.symbol} id={optId(i)} role="option" aria-selected={i === pos} className={`ts-row ${i === pos ? 'on' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); go(t.symbol); }} onMouseMove={() => setActive(i)}>
                  <b className="ts-sym">{t.symbol}</b>
                  <span className="ts-name">{t.name ?? ''}</span>
                  <span className="ts-meta">{t.trades} trade{t.trades === 1 ? '' : 's'}</span>
                  <b className={`ts-pnl ${pnlClass(t.net)}`}>{t.net > 0 ? '+' : ''}{money(t.net)}</b>
                </div>
              );
            })}
          </>
        )}
      </>
    );
  }

  return (
    <form className={`ticker-search ${open ? 'open' : ''} ${className ?? ''}`} role="search"
      onSubmit={(e) => { e.preventDefault(); const pick = options[pos] ?? query; if (pick) go(pick); }}>
      <div className="ts-bar">
        <Icon.search />
        <input ref={input} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Search a ticker or company"
          aria-label="Search tickers" role="combobox" aria-expanded={!!panel} aria-controls="ts-panel" aria-autocomplete="list"
          aria-activedescendant={panel && options[pos] ? optId(pos) : undefined}
          autoCapitalize="characters" autoComplete="off" spellCheck={false} autoFocus={autoFocus}
          onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive(Math.min(options.length - 1, pos + 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(0, pos - 1)); }
            if (e.key === 'Escape') { e.preventDefault(); if (q) setQ(''); else close(); }
          }} />
        {hotkey && (open
          ? <kbd className="ts-key hide-mobile">Esc</kbd>
          : <span className="ts-keys hide-mobile" aria-hidden="true"><kbd className="ts-key">{MAC ? '⌘' : 'Ctrl'}</kbd><kbd className="ts-key">K</kbd></span>)}
      </div>
      <span className="sr-only" role="status">{open && query && hits?.q === query ? `${hits.list.length} match${hits.list.length === 1 ? '' : 'es'}` : ''}</span>
      {panel && (
        <div className="ts-panel" id="ts-panel" role="listbox" aria-label="Tickers">
          {panel}
          <div className="ts-foot hide-mobile" aria-hidden="true">
            {[['↑↓', 'move'], ['Enter', 'open'], ['Esc', 'close']].map(([k, label]) => <span key={k}><kbd className="ts-key">{k}</kbd>{label}</span>)}
          </div>
        </div>
      )}
    </form>
  );
}
